<template>
	<div class="grid p-fluid">
		<div class="col-12 md:col-6">
			<div class="card">
				<div class="section-header">
					<h5 class="section-title">Información general</h5>
					<p class="section-desc">Los datos con los que el SRI identifica a tu negocio en tus comprobantes.</p>
				</div>
				<div class="formgrid grid">
					<div class="field col-12 lg:col-6">
						<label for="registeredName" class="field-label">Razón social</label>
						<InputText id="registeredName" type="text" placeholder="Razón social" v-model="business.registeredName" />
					</div>
					<div class="field col-12 lg:col-6">
						<label for="identification" class="field-label">RUC</label>
						<InputText id="identification" type="text" placeholder="RUC" v-model="business.identification" />
					</div>
					<div class="field col-12 lg:col-6">
						<label for="contribuitorType" class="field-label">Tipo de contribuyente</label>
						<Dropdown inputId="contribuitorType" v-model="business.contribuitorType" :options="business.contribuitorTypes" optionLabel="name" placeholder="Selecciona" />
					</div>
					<div class="field col-12 lg:col-6">
						<label for="contribuitorCode" class="field-label">Código de contribuyente</label>
						<InputText id="contribuitorCode" type="text" placeholder="Código" v-model="business.contribuitorCode" />
					</div>
					<!-- De solo lectura (H53): se cambia en la tarjeta de establecimientos, donde vive. -->
					<div class="field col-12">
						<label for="mainAddress" class="field-label">Dirección (matriz)</label>
						<div class="readonly-with-action">
							<InputText id="mainAddress" type="text" :modelValue="mainAddress || 'Sin dirección registrada'" readonly aria-describedby="mainAddressHelp" />
							<Button v-if="mainEstablishment" label="Editar" class="p-button-text p-button-sm" aria-label="Editar la dirección de la matriz" @click="editMainAddress" />
						</div>
						<small id="mainAddressHelp" class="field-help">Es la dirección del establecimiento {{ mainEstablishment ? mainEstablishment.code : '001' }}. Sale como «Dirección matriz» en todos tus comprobantes.</small>
					</div>
					<div v-if="business.rucDocumentUrl" class="field col-12">
						<a :href="baseUrl + business.rucDocumentUrl" target="_blank" rel="noopener" class="ruc-document-link">
							<i class="pi pi-file-pdf" aria-hidden="true"></i> Ver certificado de RUC
						</a>
					</div>
				</div>
				<div class="setting-row">
					<div>
						<label for="hasAccounting" class="field-label">Obligado a llevar contabilidad</label>
						<small class="field-help">Se indica así en tus comprobantes electrónicos.</small>
					</div>
					<div class="switch-control">
						<span class="switch-state" aria-hidden="true">{{ business.hasAccounting ? 'Sí' : 'No' }}</span>
						<InputSwitch id="hasAccounting" v-model="business.hasAccounting" />
					</div>
				</div>
			</div>

			<!-- Destino de ?focus=certificate (aviso de firma que no abre o vencida, al facturar). -->
			<div ref="certificateSection" class="card" :class="{'focus-highlight': focusedSection === 'certificate'}">
				<div class="section-header">
					<h5 class="section-title">Firma electrónica</h5>
					<p class="section-desc">Con ella se firman tus comprobantes antes de enviarlos al SRI.</p>
				</div>
				<div class="cert-box" :class="{'cert-box-invalid': certError && certError.field === 'certFile'}">
					<i class="pi pi-shield cert-icon" aria-hidden="true"></i>
					<div class="cert-info">
						<template v-if="business.certFile">
							<span class="cert-name">{{ business.certFile.name }}</span>
							<span class="status-badge status-info"><i class="pi pi-upload" aria-hidden="true"></i>Se cargará al guardar</span>
						</template>
						<template v-else-if="hasCertificate">
							<span class="cert-name">{{ business.certName }}</span>
							<span v-if="business.certificateStatus" :class="['status-badge', 'status-' + business.certificateStatus.severity]">
								<i :class="['pi', certificateStatusIcon]" aria-hidden="true"></i>{{ business.certificateStatus.label }}
							</span>
						</template>
						<span v-else class="cert-empty">Aún no has cargado tu firma electrónica.</span>
					</div>
					<Button :label="(hasCertificate || business.certFile) ? 'Reemplazar' : 'Cargar archivo .p12'" icon="pi pi-upload" class="p-button-outlined p-button-sm cert-action" @click="$refs.certInput.click()" />
					<input ref="certInput" type="file" accept=".p12" class="hidden" @change="onCertificateChanged($event)" />
				</div>
				<div class="formgrid grid">
					<div class="field col-12 lg:col-6">
						<label for="certPass" class="field-label">Contraseña del certificado</label>
						<!-- new-password: que el navegador no rellene con la contraseña del login de Contablus. -->
						<Password id="certPass" v-model="business.certPass" autocomplete="new-password" :feedback="false" :placeholder="business.hasCertificatePass ? '••••••••' : 'Contraseña'" :toggleMask="true" inputClass="w-full" :class="{'p-invalid': certError && certError.field === 'certPass'}" aria-describedby="certPassHelp"></Password>
						<small v-if="certError" id="certPassHelp" class="field-error" role="alert">{{ certError.message }}</small>
						<small v-else-if="business.hasCertificatePass" id="certPassHelp" class="field-help">Hay una contraseña guardada. Escribe una nueva solo si cambias de certificado o de contraseña.</small>
						<small v-else id="certPassHelp" class="field-help">La que recibiste junto con el archivo .p12.</small>
					</div>
				</div>
			</div>

			<!-- Destino de ?focus=sri-portal (avisos de credenciales de ventas y compras). -->
			<div ref="sriPortalSection" class="card" :class="{'focus-highlight': focusedSection === 'sri-portal'}">
				<div class="section-header">
					<h5 class="section-title">Portal SRI en línea</h5>
					<p class="section-desc">Utilizamos tus credenciales del portal SRI en línea únicamente para solicitar, de forma automatizada y en tu nombre, la anulación de tus comprobantes electrónicos. Son opcionales: sin ellas puedes anular igual, haciendo tú mismo el trámite en el portal del SRI. Corresponden a tu acceso al portal del SRI y son <b>distintas</b> de la contraseña del certificado de firma.</p>
				</div>
				<div class="formgrid grid">
					<div class="field col-12 lg:col-6">
						<label for="sriPortalUser" class="field-label">Usuario (RUC o cédula)</label>
						<!-- Usuario + clave parecen un login: sin esto el navegador los llenaba con el
						correo y la contraseña de Contablus, y el correo no cabe como usuario. -->
						<InputText id="sriPortalUser" v-model="business.sriPortalUser" autocomplete="off" placeholder="RUC o cédula de acceso al portal" />
					</div>
					<div class="field col-12 lg:col-6">
						<label for="sriPortalPass" class="field-label">Clave del portal</label>
						<Password id="sriPortalPass" v-model="business.sriPortalPass" autocomplete="new-password" :feedback="false" :placeholder="business.hasSriPortalCredentials ? '••••••••' : ''" :toggleMask="true" inputClass="w-full"></Password>
						<small v-if="business.hasSriPortalCredentials" class="field-help">Hay una clave guardada. Escribe una nueva solo si quieres cambiarla.</small>
					</div>
					<!-- Dejar los campos vacíos conserva lo guardado: borrar es una acción aparte (H38). -->
					<div v-if="business.hasSriPortalCredentials" class="field col-12">
						<ConfirmDialog group="sriPortal"/>
						<Button label="Quitar credenciales" icon="pi pi-trash" class="p-button-outlined p-button-danger p-button-sm" @click="confirmClearSriPortal"/>
					</div>
				</div>
			</div>

			<div class="form-actions">
				<Toast />
				<Button label="Guardar cambios" icon="pi pi-check" class="p-button-success save-button" @click="this.save()"/>
			</div>
		</div>
		<div class="col-12 md:col-6">
			<div ref="establishmentsSection">
				<EstablishmentsCard ref="establishments" :establishments="establishments" :nextEstablishmentCode="nextEstablishmentCode"
					:loaded="activitiesLoaded" @updated="onActivitiesPayload" />
			</div>

			<EconActivitiesCard :activities="activities" :establishments="establishments" :nextEstablishmentCode="nextEstablishmentCode"
				:activityLimit="activityLimit" :activityRemaining="activityRemaining" :loaded="activitiesLoaded"
				@updated="onActivitiesPayload" @fix-establishment="openEstablishment" />

			<div class="card">
				<div class="section-header section-header-action">
					<div>
						<h5 class="section-title"><label for="makesRetention">Agente de retención</label></h5>
						<p class="section-desc">Actívalo si el SRI te designó como agente de retención.</p>
					</div>
					<div class="switch-control">
						<span class="switch-state" aria-hidden="true">{{ business.makesRetention ? 'Sí' : 'No' }}</span>
						<InputSwitch id="makesRetention" v-model="business.makesRetention" />
					</div>
				</div>
				<div class="form-actions form-actions-inset">
					<Button label="Guardar cambios" icon="pi pi-check" class="p-button-success save-button" @click="this.save()"/>
				</div>
			</div>
		</div>

	</div>
</template>
<script>
	import httpRequest from '../../service/httpRequest';	
	import Business from '../../models/business';
	import EstablishmentsCard from './EstablishmentsCard.vue';
	import EconActivitiesCard from './EconActivitiesCard.vue';

	// Secciones a las que otra pantalla puede llevar al usuario con ?focus=<clave>
	// (clave -> ref del template). Solo se aceptan las de esta lista.
	const FOCUS_SECTIONS = {
		'sri-portal': 'sriPortalSection',
		'certificate': 'certificateSection',
		'establishments': 'establishmentsSection',
	};

	const CERTIFICATE_STATUS_ICONS = {
		success: 'pi-check-circle',
		warning: 'pi-exclamation-triangle',
		danger: 'pi-times-circle',
	};

	export default {
		components: { EstablishmentsCard, EconActivitiesCard },
		data() {
			return {
				business: new Business(),
				focusedSection: null,
				// Rechazo de la firma al guardar: {field: 'certPass'|'certFile', message}.
				certError: null,
				baseUrl: this.$baseURL,
				// Lo que devuelve activity/get-all (y cada guardado de las dos tarjetas).
				activities: [],
				establishments: [],
				nextEstablishmentCode: null,
				activitiesLoaded: false,
				// Cupo de actividades del plan (gate de UI; el backend es el gate duro). null = ilimitado.
				activityLimit: null,
				activityRemaining: null,
			}
		},
		watch: {
			// Al corregir la contraseña se quita el error; el backend vuelve a validar.
			'business.certPass'() {
				if (this.certError && this.certError.field === 'certPass') { this.certError = null }
			},
		},
		computed: {
			// Sin certificado el backend no manda certName y el modelo deja "Seleccionar".
			hasCertificate() {
				return !!this.business.certName && this.business.certName !== 'Seleccionar';
			},
			certificateStatusIcon() {
				const status = this.business.certificateStatus;
				return status ? CERTIFICATE_STATUS_ICONS[status.severity] : null;
			},
			mainEstablishment() {
				return this.establishments.find(e => e.isMain) || null;
			},
			// La de la tarjeta de establecimientos en cuanto llega (se puede editar ahí).
			mainAddress() {
				return this.mainEstablishment ? this.mainEstablishment.address : this.business.mainAddress;
			},
		},
		created() {
			// Se enfoca después de cargar todo: los datos que llegan cambian la altura de
			// las tarjetas, y si no el scroll quedaría corrido.
			Promise.all([this.getBusiness(), this.getEconActivities()]).then(() => this.focusSectionFromRoute());
		},
		methods: {
			// Centra en pantalla la sección que pide ?focus=, la resalta un momento y
			// deja el cursor en su primer campo vacío, para que el usuario vea de
			// inmediato qué tiene que llenar.
			focusSectionFromRoute() {
				const key = this.$route.query.focus;
				if (!FOCUS_SECTIONS[key]) { return }
				this.$nextTick(() => {
					const section = this.$refs[FOCUS_SECTIONS[key]];
					if (!section) { return }  // p. ej. ya se fue a /login
					const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
					section.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'center'});
					// Desde el aviso 604: la tarjeta abre el establecimiento sin dirección.
					if (key === 'establishments') {
						this.$refs.establishments.focusMissing();
						return;
					}
					// Sin el selector de archivo oculto de la firma: no recibe el foco.
					const inputs = Array.from(section.querySelectorAll('input:not([type="file"])'));
					const input = inputs.find(i => !i.value) || inputs[0];
					if (input) { input.focus({preventScroll: true}) }
					this.focusedSection = key;
				});
			},
			async getBusiness() {
				let response = await httpRequest.getBusiness();
				if (response.status == 200 && response.data !== null) {
					let business = new Business(response.data);
					this.business = business;
				} else if (response.status == 502) { // Unauthorized
					await this.$store.dispatch('Logout');
					this.$router.push('/login');
				}
			},
			async save() {
				const form = new FormData();
				if (this.business.registeredName !== null) { form.append('registeredName', this.business.registeredName) }
				if (this.business.identification !== null) { form.append('identification', this.business.identification) }
				if (this.business.contribuitorType !== null) { form.append('contribuitorType', this.business.contribuitorType.value) }
				if (this.business.contribuitorCode !== null) { form.append('contribuitorCode', this.business.contribuitorCode) }
				if (this.business.hasAccounting !== null) { form.append('hasAccounting', this.business.hasAccounting) }
				if (this.business.makesRetention !== null) { form.append('makesRetention', this.business.makesRetention) }
				if (this.business.logoFile !== null) { form.append('logoFile', this.business.logoFile) }
				if (this.business.certFile !== null) { form.append('certFile', this.business.certFile) }
				if (this.business.certPass !== null) { form.append('certPass', this.business.certPass) }
				if (this.business.sriPortalUser !== null) { form.append('sriPortalUser', this.business.sriPortalUser) }
				if (this.business.sriPortalPass !== null) { form.append('sriPortalPass', this.business.sriPortalPass) }

				let response = await httpRequest.saveBusiness(form);
				if (response.status === 200) {
					let business = new Business(response.data);
					this.business = business;
					this.certError = null;
					await this.$store.dispatch('setBusiness', business);
					this.$toast.add({severity:'success', summary: 'Guardado', detail:'Los datos se actualizaron con éxito', life: 3000});
					// Las credenciales del portal son opcionales: si no eran válidas el
					// backend guarda todo lo demás y avisa que esas quedaron afuera.
					if (response.warning) {
						this.$toast.add({severity:'warn', summary: 'Portal SRI en línea', detail: response.warning, life: 10000});
					}
				} else if (response.status === 422 && response.error) {
					// La contraseña no abre el .p12: el backend no guardó nada. Se
					// conserva lo escrito y el archivo elegido para que lo corrija.
					const field = response.data && response.data.field;
					if (field === 'certPass' || field === 'certFile') {
						this.certError = { field: field, message: response.error };
						this.revealCertificateSection(field);
					}
					this.$toast.add({severity:'error', summary: 'No se guardaron los cambios', detail: response.error, life: 8000});
				} else {
					// Nunca en silencio: un 500 aquí se veía como "Guardar no hace nada".
					this.$toast.add({severity:'error', summary: 'No se guardaron los cambios', detail: response.error || 'Ocurrió un problema al guardar. Intenta de nuevo.', life: 8000});
				}
			},
			confirmClearSriPortal() {
				this.$confirm.require({
					group: 'sriPortal',
					message: '¿Quitar las credenciales del portal del SRI?',
					header: 'Portal SRI en línea',
					icon: 'pi pi-exclamation-triangle',
					acceptLabel: 'Sí',
					rejectLabel: 'No',
					accept: () => this.clearSriPortal(),
				});
			},
			async clearSriPortal() {
				const form = new FormData();
				form.append('clearSriPortalCredentials', 'true');
				const response = await httpRequest.saveBusiness(form);
				if (response.status === 200 && response.data) {
					const business = new Business(response.data);
					this.business = business;
					await this.$store.dispatch('setBusiness', business);
					this.$toast.add({severity:'success', summary: 'Portal SRI en línea', detail: 'Se quitaron las credenciales.', life: 4000});
				} else {
					this.$toast.add({severity:'error', summary: 'No se quitaron las credenciales', detail: response.error || 'Intenta de nuevo.', life: 8000});
				}
			},
			// El botón Guardar queda lejos de la firma: la lleva a la vista y, si el
			// problema es la contraseña, deja el cursor en ese campo.
			revealCertificateSection(field) {
				this.$nextTick(() => {
					const section = this.$refs.certificateSection;
					if (!section) { return }
					const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
					section.scrollIntoView({behavior: reduceMotion ? 'auto' : 'smooth', block: 'center'});
					const input = field === 'certPass' ? section.querySelector('#certPass') : null;
					if (input) { input.focus({preventScroll: true}) }
				});
			},
			async getEconActivities() {
				const response = await httpRequest.getEconActivities();
				if (response.status === 200) {
					this.onActivitiesPayload(response.data);
				} else if (response.status !== 502) {
					this.$toast.add({severity: 'error', summary: 'No se cargaron las actividades', life: 8000,
						detail: 'No pudimos traer tus establecimientos y actividades. Recarga la página.'});
				}
			},
			// Respuesta de activity/get-all y de cada guardado de las tarjetas. Crear o editar una
			// actividad no cambia la actividad por defecto del usuario: eso es del selector del topbar.
			onActivitiesPayload(data) {
				this.activities = data.activities || [];
				this.establishments = data.establishments || [];
				this.nextEstablishmentCode = data.nextEstablishmentCode || null;
				this.activityLimit = (data.activityLimit !== undefined && data.activityLimit !== null) ? data.activityLimit : null;
				this.activityRemaining = (data.activityRemaining !== undefined && data.activityRemaining !== null) ? data.activityRemaining : null;
				this.activitiesLoaded = true;
			},
			openEstablishment(id) {
				const section = this.$refs.establishmentsSection;
				if (section) { section.scrollIntoView({block: 'start'}) }
				this.$refs.establishments.openEstablishmentById(id);
			},
			editMainAddress() {
				if (this.mainEstablishment) { this.$refs.establishments.openEditEstablishment(this.mainEstablishment) }
			},
			onCertificateChanged(event) {
				const file = event.target.files[0];
				if (!file) { return }
				this.business.certFile = file;
				this.certError = null;
				// Limpia el input para que volver a elegir el mismo archivo dispare @change.
				event.target.value = '';
			},
		}
	}
</script>
<style scoped>
	/* Encabezado de cada tarjeta: título + una línea que explica para qué sirve. */
	.section-header {
		margin-bottom: 1.25rem;
	}
	.section-header-action {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		gap: 1rem;
	}
	.section-title {
		font-size: 1.25rem;
		font-weight: 600;
		margin: 0 0 0.25rem 0;
		color: inherit;
	}
	.section-desc {
		margin: 0;
		line-height: 1.5;
		color: var(--text-color-secondary);
	}

	/* Etiquetas más discretas que los títulos, para que la jerarquía se lea sola. */
	.field-label {
		display: block;
		font-weight: 500;
		margin-bottom: 0.5rem;
		color: var(--text-color);
	}
	.field-help {
		display: block;
		margin-top: 0.375rem;
		font-size: 0.9rem;
		line-height: 1.4;
		color: var(--text-color-secondary);
	}
	.field-error {
		display: block;
		margin-top: 0.375rem;
		font-size: 0.9rem;
		line-height: 1.4;
		font-weight: 500;
		color: #B91C1C;
	}
	/* El padding de la tarjeta ya da el aire inferior. */
	.card > .formgrid:last-child > .field:last-child {
		margin-bottom: 0;
	}

	.setting-row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 1rem;
		padding-top: 1rem;
		border-top: 1px solid var(--surface-border);
	}
	.setting-row .field-label {
		margin-bottom: 0.125rem;
	}
	.setting-row .field-help {
		margin-top: 0;
	}
	.switch-control {
		display: flex;
		align-items: center;
		gap: 0.75rem;
		flex-shrink: 0;
	}
	.switch-state {
		min-width: 1.5rem;
		text-align: right;
		font-weight: 500;
		color: var(--text-color-secondary);
	}

	.cert-box {
		display: flex;
		align-items: center;
		gap: 1rem;
		padding: 1rem;
		margin-bottom: 1.25rem;
		border: 1px solid var(--surface-border);
		border-radius: 10px;
		background: var(--surface-ground);
	}
	/* Mismo rojo que p-invalid del tema. */
	.cert-box-invalid {
		border-color: #e24c4c;
	}
	.cert-icon {
		font-size: 1.5rem;
		color: var(--primary-color);
		flex-shrink: 0;
	}
	.cert-info {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: 0.375rem;
	}
	.cert-name {
		font-weight: 500;
		color: var(--text-color);
		word-break: break-all;
	}
	.cert-empty {
		color: var(--text-color-secondary);
	}
	.readonly-with-action {
		display: flex;
		align-items: center;
		gap: 0.5rem;
	}
	.readonly-with-action .p-inputtext {
		flex: 1;
		min-width: 0;
	}
	.readonly-with-action .p-button {
		width: auto;
		flex-shrink: 0;
	}
	.ruc-document-link {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		color: var(--primary-color);
		font-weight: 500;
	}
	.cert-box .cert-action {
		width: auto;
		flex-shrink: 0;
	}

	/* Colores fijos (fondo claro + texto oscuro) con contraste AA en ambos temas. */
	.status-badge {
		display: inline-flex;
		align-items: center;
		gap: 0.375rem;
		padding: 0.25rem 0.625rem;
		border-radius: 6px;
		font-size: 0.9rem;
		font-weight: 600;
		line-height: 1.3;
	}
	.status-badge .pi {
		font-size: 0.85rem;
	}
	.status-success { background: #DCFCE7; color: #166534; }
	.status-warning { background: #FEF3C7; color: #92400E; }
	.status-danger { background: #FEE2E2; color: #991B1B; }
	.status-info { background: #E0E7FF; color: #3730A3; }

	.form-actions {
		display: flex;
		justify-content: flex-end;
		margin-bottom: 1rem;
	}
	.form-actions-inset {
		margin: 1.25rem 0 0 0;
	}
	.form-actions .save-button {
		width: auto;
		min-width: 12rem;
	}

	@media (max-width: 575px) {
		.cert-box {
			flex-wrap: wrap;
		}
		.cert-box .cert-action,
		.form-actions .save-button {
			width: 100%;
			justify-content: center;
		}
		/* PrimeVue estira la etiqueta y deja el ícono pegado al borde izquierdo. */
		.cert-box .cert-action :deep(.p-button-label),
		.form-actions .save-button :deep(.p-button-label) {
			flex: 0 0 auto;
		}
	}

	/* La animación corre una vez al poner la clase y se desvanece sola. */
	.focus-highlight {
		animation: focus-highlight 2.5s ease-out;
	}
	@keyframes focus-highlight {
		0%, 50% { outline: 2px solid var(--primary-color); outline-offset: 4px; }
		100% { outline: 2px solid transparent; outline-offset: 4px; }
	}
</style>